/* ===== RESET & BASE ===== */
*, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }
html { scroll-behavior: smooth; }
body {
  font-family: 'Segoe UI', Tahoma, Geneva, sans-serif;
  background: var(--bg);
  color: var(--text);
  transition: background 0.3s, color 0.3s;
  min-height: 100vh;
}

/* ===== CSS VARIABLES ===== */
:root {
  --primary: #1a4a7a;
  --primary-dark: #0f2d4f;
  --primary-light: #2563a8;
  --accent: #e87722;
  --accent-dark: #c5601a;
  --accent-light: #f59340;
  --bg: #f7f8fa;
  --bg-card: #ffffff;
  --bg-section: #eef2f7;
  --text: #1a2332;
  --text-muted: #5a6a7e;
  --text-light: #8a9ab0;
  --text-soft: var(--text-muted);
  --border: #d0dae7;
  --shadow: 0 4px 24px rgba(26,74,122,0.10);
  --shadow-lg: 0 12px 48px rgba(26,74,122,0.16);
  --radius: 14px;
  --radius-sm: 8px;

  /* ===== LOGO-DERIVED NAVY COLOR SYSTEM =====
     Extracted from logo.png deep navy #023151 (h≈204°, s≈95%, l≈16%).
     Constrained 9-shade scale per refactoring-ui principle #4.
  */
  --navy-50: #f0f4f8;
  --navy-100: #dbe3ee;
  --navy-200: #b9cbd8;
  --navy-300: #8da9bd;
  --navy-400: #5e809c;
  --navy-500: #023151;
  --navy-600: #02253d;
  --navy-700: #02192a;
  --navy-800: #01101b;
  --navy-900: #00080d;

  /* Neutral text scale (grayscale-first hierarchy) */
  --ink-dark: #0f172a;
  --ink-base: #334155;
  --ink-muted: #64748b;
  --ink-light: #94a3b8;

  /* Depth system */
  --shadow-sm: 0 1px 3px rgba(2,49,81,0.08);
  --shadow-md: 0 4px 12px rgba(2,49,81,0.10);
  --shadow-lg: 0 12px 40px rgba(2,49,81,0.14);
  --shadow-xl: 0 20px 60px rgba(2,49,81,0.20);

  /* Spacing scale (refactoring-ui #2): 4,8,16,24,32,48,64 */
  --space-1: 4px;
  --space-2: 8px;
  --space-3: 12px;
  --space-4: 16px;
  --space-5: 20px;
  --space-6: 24px;
  --space-8: 32px;
  --space-10: 40px;
  --space-12: 48px;
  --space-16: 64px;

  --radius: 16px;
  --radius-lg: 24px;
  --navbar-h: 72px;
}
.dark {
  --bg: #0f1923;
  --bg-card: #1a2638;
  --bg-section: #152030;
  --text: #e8edf4;
  --text-muted: #8fa5c0;
  --text-light: #5a7a9a;
  --border: #253547;
  --shadow: 0 4px 24px rgba(0,0,0,0.30);
  --shadow-lg: 0 12px 48px rgba(0,0,0,0.40);
}

/* ===== UTILITY ===== */
.btn {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 12px 28px;
  border-radius: 50px;
  font-size: 15px;
  font-weight: 600;
  cursor: pointer;
  border: none;
  text-decoration: none;
  transition: all 0.22s ease;
}
.btn-primary { background: var(--accent); color: #fff; }
.btn-primary:hover { background: var(--accent-dark); transform: translateY(-2px); box-shadow: 0 6px 20px rgba(232,119,34,0.35); }
.btn-outline { background: transparent; color: #fff; border: 2px solid rgba(255,255,255,0.7); }
.btn-outline:hover { background: rgba(255,255,255,0.15); }
.btn-white { background: #fff; color: var(--primary); font-weight: 700; }
.btn-white:hover { transform: translateY(-2px); box-shadow: var(--shadow); }

/* ===== NAVBAR ===== */
.navbar {
  position: fixed;
  top: 0; left: 0; right: 0;
  z-index: 999;
  background: rgba(15, 29, 47, 0.97);
  backdrop-filter: blur(12px);
  box-shadow: 0 2px 20px rgba(0,0,0,0.18);
  height: var(--navbar-h);
}
.nav-inner {
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 24px;
  height: 100%;
  display: flex;
  align-items: center;
  gap: 24px;
}
.nav-logo {
  display: flex;
  align-items: center;
  gap: 10px;
  text-decoration: none;
  flex-shrink: 0;
}
.logo-img { width: 46px; height: 46px; object-fit: contain; border-radius: 8px; }
.logo-text { display: flex; flex-direction: column; line-height: 1.1; }
.logo-main { color: #fff; font-size: 18px; font-weight: 800; }
.logo-sub { color: var(--accent-light); font-size: 11px; font-weight: 500; }
.nav-links { display: flex; list-style: none; gap: 4px; margin: 0 auto; }
.nav-link {
  color: rgba(255,255,255,0.82);
  text-decoration: none;
  padding: 8px 16px;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 500;
  transition: all 0.2s;
}
.nav-link:hover, .nav-link.active { color: #fff; background: rgba(255,255,255,0.1); }
.nav-link.active { color: var(--accent-light); }
.nav-actions { display: flex; align-items: center; gap: 8px; flex-shrink: 0; }
.nav-btn {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 8px 14px;
  border-radius: 8px;
  border: none;
  cursor: pointer;
  font-size: 13px;
  font-weight: 600;
  transition: all 0.2s;
  background: rgba(255,255,255,0.08);
  color: rgba(255,255,255,0.85);
}
.nav-btn:hover { background: rgba(255,255,255,0.15); color: #fff; }
.icon-btn { padding: 8px 10px; }
.lang-btn { border: 1px solid rgba(255,255,255,0.2); }
.chat-btn { background: var(--accent); color: #fff; padding: 8px 14px; }
.chat-btn:hover { background: var(--accent-dark); }
.mobile-menu-btn { display: none; background: transparent; border: none; color: #fff; cursor: pointer; padding: 6px; }
.nav-overlay { display: none; }

@media (max-width: 768px) {
  .nav-links {
    position: absolute;
    top: 100%;
    left: 0; right: 0;
    background: #0f1d2f;
    flex-direction: column;
    padding: 0 16px;
    gap: 4px;
    max-height: 0;
    overflow: hidden;
    transition: max-height 0.3s ease, padding 0.3s ease;
    z-index: 998;
    box-shadow: 0 8px 24px rgba(0,0,0,0.3);
  }
  .nav-links.open {
    max-height: 400px;
    padding: 12px 16px;
  }
  .nav-overlay { display: block; position: fixed; inset: 0; z-index: 997; top: var(--navbar-h); }
  .mobile-menu-btn { display: flex; }
  .chat-label { display: none; }
  .lang-btn span { display: none; }
}

/* ===== PAGE BASE ===== */
.page { padding-top: var(--navbar-h); }

/* ===== SECTION ===== */
.section { padding: 80px 0; }
.section:nth-child(even) { background: var(--bg-section); }
.section-inner { max-width: 1200px; margin: 0 auto; padding: 0 24px; }
.section-header { text-align: center; margin-bottom: 56px; }
.section-header h2 { font-size: clamp(26px, 3.5vw, 36px); color: var(--primary); font-weight: 800; margin-bottom: 12px; }
.dark .section-header h2 { color: var(--accent-light); }
.section-header p { color: var(--text-muted); font-size: 17px; max-width: 560px; margin: 0 auto; }

/* ===== HERO ===== */
.hero-section { position: relative; min-height: 95vh; display: flex; align-items: center; overflow: hidden; }
.hero-bg { position: absolute; inset: 0; }
.hero-bg-img { width: 100%; height: 100%; object-fit: cover; }
.hero-overlay {
  position: absolute; inset: 0;
  background: linear-gradient(135deg, rgba(10,28,50,0.92) 0%, rgba(26,74,122,0.75) 60%, rgba(232,119,34,0.25) 100%);
}
.hero-content {
  position: relative;
  z-index: 2;
  max-width: 720px;
  padding: 60px 5vw;
}
.hero-tagline {
  color: var(--accent-light);
  font-size: 14px;
  font-weight: 600;
  letter-spacing: 3px;
  text-transform: uppercase;
  margin-bottom: 16px;
  display: flex;
  align-items: center;
  gap: 10px;
}
.hero-tagline::before { content: ''; display: inline-block; width: 32px; height: 2px; background: var(--accent); }
.hero-title { color: var(--accent-light); font-size: clamp(34px, 5vw, 60px); font-weight: 900; line-height: 1.15; margin-bottom: 20px; }
.hero-desc { color: var(--accent-light); font-size: 20px; font-weight: 500; line-height: 1.8; margin-bottom: 36px; max-width: 560px; }
.hero-btns { display: flex; gap: 16px; flex-wrap: wrap; }

/* ===== SERVICES ===== */
.services-grid { display: grid; grid-template-columns: repeat(auto-fit, minmax(250px, 1fr)); gap: 24px; }
.service-card {
  background: var(--bg-card);
  border-radius: var(--radius);
  padding: 36px 28px;
  box-shadow: var(--shadow);
  border: 1px solid var(--border);
  transition: transform 0.25s, box-shadow 0.25s;
  text-align: center;
}
.service-card:hover { transform: translateY(-6px); box-shadow: var(--shadow-lg); }
.service-icon {
  width: 72px; height: 72px;
  border-radius: 16px;
  background: linear-gradient(135deg, var(--primary) 0%, var(--accent) 100%);
  display: flex; align-items: center; justify-content: center;
  margin: 0 auto 20px;
  color: #fff;
}
.service-card h3 { font-size: 18px; font-weight: 700; color: var(--text); margin-bottom: 12px; }
.service-card p { color: var(--text-muted); font-size: 15px; line-height: 1.7; }

/* ===== PORTS ===== */
.ports-section { background: var(--bg-section) !important; }
.ports-grid { display: grid; grid-template-columns: repeat(auto-fit, minmax(300px, 1fr)); gap: 24px; }
.port-card { border-radius: var(--radius); overflow: hidden; box-shadow: var(--shadow); background: var(--bg-card); transition: transform 0.25s, box-shadow 0.25s; }
.port-card:hover { transform: translateY(-6px); box-shadow: var(--shadow-lg); }
.port-img-wrap { position: relative; height: 200px; overflow: hidden; }
.port-img { width: 100%; height: 100%; object-fit: cover; transition: transform 0.4s; }
.port-card:hover .port-img { transform: scale(1.06); }
.port-img-overlay { position: absolute; inset: 0; background: linear-gradient(to bottom, transparent 40%, rgba(10,28,50,0.7)); }
.port-info { padding: 24px; }
.port-icon { color: var(--accent); margin-bottom: 10px; }
.port-info h3 { font-size: 18px; font-weight: 700; color: var(--text); margin-bottom: 8px; }
.port-info p { color: var(--text-muted); font-size: 14px; line-height: 1.6; }

/* ===== FREE ZONES ===== */
.freezones-section { background: var(--bg-section) !important; }
.freezones-grid { display: grid; grid-template-columns: repeat(auto-fit, minmax(240px, 1fr)); gap: 24px; }
.freezone-card {
  background: var(--bg-card); border-radius: var(--radius); padding: 32px 24px;
  box-shadow: var(--shadow); border: 1px solid var(--border);
  text-align: center; transition: transform 0.25s, box-shadow 0.25s;
}
.freezone-card:hover { transform: translateY(-6px); box-shadow: var(--shadow-lg); }
.freezone-icon {
  width: 64px; height: 64px; border-radius: 14px;
  background: linear-gradient(135deg, var(--primary), var(--accent));
  display: flex; align-items: center; justify-content: center;
  margin: 0 auto 16px; color: #fff;
}
.freezone-card h3 { font-size: 17px; font-weight: 700; color: var(--text); margin-bottom: 10px; }
.freezone-card p { color: var(--text-muted); font-size: 14px; line-height: 1.6; }

/* ===== CLIENTS ===== */
.clients-section { background: var(--bg) !important; }
.clients-grid {
  display: grid; grid-template-columns: repeat(2, 1fr); gap: 32px;
  max-width: 720px; margin: 0 auto;
}
@media (max-width: 600px) { .clients-grid { grid-template-columns: 1fr; } }
.client-card {
  background: var(--bg-card); border-radius: var(--radius); padding: 40px 28px;
  box-shadow: var(--shadow); border: 1px solid var(--border);
  text-align: center; transition: transform 0.25s, box-shadow 0.25s;
}
.client-card:hover { transform: translateY(-6px); box-shadow: var(--shadow-lg); }
.client-icon {
  width: 80px; height: 80px; border-radius: 50%;
  background: linear-gradient(135deg, var(--primary), var(--accent));
  display: flex; align-items: center; justify-content: center;
  margin: 0 auto 20px; color: #fff; padding: 16px;
}
.client-card h3 { font-size: 20px; font-weight: 700; color: var(--text); margin-bottom: 12px; }
.client-card p { color: var(--text-muted); font-size: 15px; line-height: 1.7; }

.clients-home-cta { text-align: center; margin-top: 40px; }

.clients-marquee-wrap {
  overflow: hidden;
  mask-image: linear-gradient(to right, transparent, #000 5%, #000 95%, transparent);
  -webkit-mask-image: linear-gradient(to right, transparent, #000 5%, #000 95%, transparent);
}
.clients-marquee-wrap:hover .clients-marquee-track {
  animation-play-state: paused;
}
.clients-marquee-track {
  display: flex;
  gap: 48px;
  width: max-content;
  animation: marquee-scroll 120s linear infinite;
}
.clients-marquee-group {
  display: flex;
  gap: 48px;
  align-items: center;
}
.clients-marquee-logo {
  height: 52px;
  width: auto;
  max-width: 140px;
  object-fit: contain;
  filter: grayscale(1) brightness(1.1);
  opacity: 0.7;
  transition: opacity 0.25s, filter 0.25s;
  flex-shrink: 0;
}
.clients-marquee-logo:hover {
  opacity: 1;
  filter: none;
}
.clients-marquee-name {
  white-space: nowrap;
  font-size: 1rem;
  font-weight: 600;
  color: var(--text-muted);
  opacity: 0.8;
  transition: opacity 0.25s, color 0.25s;
  flex-shrink: 0;
}
.clients-marquee-name:hover {
  opacity: 1;
  color: var(--primary);
}
@keyframes marquee-scroll {
  0%   { transform: translateX(0); }
  100% { transform: translateX(-50%); }
}

/* ===== LAND PORTS ===== */
.landports-section { background: var(--bg-section) !important; }
.landports-grid { display: grid; grid-template-columns: repeat(auto-fit, minmax(280px, 1fr)); gap: 20px; }
.landport-card {
  display: flex; align-items: flex-start; gap: 16px;
  background: var(--bg-card); border-radius: var(--radius); padding: 24px;
  box-shadow: var(--shadow); border: 1px solid var(--border);
  transition: transform 0.25s, box-shadow 0.25s;
}
.landport-card:hover { transform: translateY(-4px); box-shadow: var(--shadow-lg); }
.landport-icon {
  width: 52px; height: 52px; border-radius: 12px; flex-shrink: 0;
  background: linear-gradient(135deg, var(--primary), var(--accent));
  display: flex; align-items: center; justify-content: center; color: #fff;
}
.landport-info h3 { font-size: 16px; font-weight: 700; color: var(--text); margin-bottom: 6px; }
.landport-info p { color: var(--text-muted); font-size: 13px; line-height: 1.6; }

/* ===== SEA PORTS ===== */
.seaports-section { background: var(--bg-section) !important; }
.seaports-grid { display: grid; grid-template-columns: repeat(auto-fit, minmax(280px, 1fr)); gap: 20px; }
.seaport-card {
  display: flex; align-items: flex-start; gap: 16px;
  background: var(--bg-card); border-radius: var(--radius); padding: 24px;
  box-shadow: var(--shadow); border: 1px solid var(--border);
  transition: transform 0.25s, box-shadow 0.25s;
}
.seaport-card:hover { transform: translateY(-4px); box-shadow: var(--shadow-lg); }
.seaport-icon {
  width: 52px; height: 52px; border-radius: 12px; flex-shrink: 0;
  background: linear-gradient(135deg, var(--primary), var(--accent));
  display: flex; align-items: center; justify-content: center; color: #fff;
}
.seaport-info h3 { font-size: 16px; font-weight: 700; color: var(--text); margin-bottom: 6px; }
.seaport-info p { color: var(--text-muted); font-size: 13px; line-height: 1.6; }

/* ===== WHY US ===== */
.why-section { background: var(--bg) !important; }
.why-inner { display: grid; grid-template-columns: 1fr 1fr; gap: 64px; align-items: center; }
@media (max-width: 768px) { .why-inner { grid-template-columns: 1fr; gap: 40px; } }
.why-text h2 { font-size: clamp(24px, 3vw, 36px); font-weight: 800; color: var(--primary); margin-bottom: 28px; }
.dark .why-text h2 { color: var(--accent-light); }
.why-list { list-style: none; margin-bottom: 36px; }
.why-item { display: flex; align-items: center; gap: 14px; padding: 10px 0; font-size: 16px; color: var(--text); border-bottom: 1px solid var(--border); }
.why-item:last-child { border-bottom: none; }
.why-check { color: var(--accent); flex-shrink: 0; }
.why-cta { margin-top: 8px; }
.why-img-wrap { border-radius: 20px; overflow: hidden; box-shadow: var(--shadow-lg); }
.why-img { width: 100%; height: 420px; object-fit: cover; }

/* ===== CTA ===== */
.cta-section {
  background: linear-gradient(135deg, var(--primary-dark) 0%, var(--primary) 50%, var(--accent) 100%);
  padding: 80px 24px;
  text-align: center;
}
.cta-inner { max-width: 700px; margin: 0 auto; }
.cta-inner h2 { color: #fff; font-size: clamp(24px, 3.5vw, 36px); font-weight: 800; margin-bottom: 16px; }
.cta-inner p { color: rgba(255,255,255,0.85); font-size: 17px; margin-bottom: 32px; }

/* ===== PAGE HERO ===== */
.page-hero { position: relative; height: 380px; display: flex; align-items: flex-end; overflow: hidden; }
.page-hero-img { position: absolute; inset: 0; width: 100%; height: 100%; object-fit: cover; z-index: 0; }
.parallax-img { will-change: transform; transform-origin: center center; }
.page-hero-overlay {
  position: absolute; inset: 0;
  background: linear-gradient(135deg, rgba(10,28,50,0.88) 0%, rgba(26,74,122,0.7) 100%);
  z-index: 1;
}
.page-hero-content {
  position: relative; z-index: 2;
  max-width: 1200px; margin: 0 auto;
  padding: 0 24px 48px; width: 100%;
}
.page-hero-content h1 { color: var(--accent-light); font-size: clamp(28px, 4vw, 44px); font-weight: 900; margin-bottom: 12px; }
.page-hero-content p { color: var(--accent-light); font-size: 20px; max-width: 560px; }

/* ===== ABOUT ===== */
@media (max-width: 700px) { .mv-card { padding: 32px 24px; } }
.mv-card { background: var(--bg-card); border-radius: var(--radius); padding: 40px; box-shadow: var(--shadow); border: 1px solid var(--border); }
.mv-icon {
  width: 60px; height: 60px;
  background: linear-gradient(135deg, var(--primary), var(--accent));
  border-radius: 14px;
  display: flex; align-items: center; justify-content: center;
  color: #fff; margin-bottom: 20px;
}
.mv-card h2 { font-size: 22px; font-weight: 800; color: var(--primary); margin-bottom: 14px; }
.dark .mv-card h2 { color: var(--accent-light); }
.mv-card p { color: var(--text-muted); line-height: 1.8; font-size: 15px; }

/* ===== MISSION & VISION ENHANCED ===== */
.mv-tag {
  font-size: 12px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 1px;
  color: var(--accent);
  margin-bottom: 10px;
  display: block;
}
.mv-card h2 { font-size: clamp(22px, 3vw, 28px); font-weight: 800; color: var(--primary); margin-bottom: 14px; }
.dark .mv-card h2 { color: var(--accent-light); }
.mv-card p { color: var(--text-muted); line-height: 1.8; font-size: 15px; }

/* ===== ARTICLES ===== */
.articles-hero { height: 360px; }
.articles-filter-wrap {
  background: var(--bg-card);
  border-bottom: 1px solid var(--border);
  padding: 20px 24px;
  position: sticky;
  top: var(--navbar-h);
  z-index: 10;
  box-shadow: var(--shadow);
}
.articles-filter { max-width: 1200px; margin: 0 auto; display: flex; gap: 10px; flex-wrap: wrap; }
.filter-btn {
  padding: 8px 20px;
  border-radius: 50px;
  border: 1.5px solid var(--border);
  background: transparent;
  color: var(--text-muted);
  font-size: 14px; font-weight: 500;
  cursor: pointer; transition: all 0.2s;
}
.filter-btn:hover { border-color: var(--accent); color: var(--accent); }
.filter-btn.active { background: var(--accent); border-color: var(--accent); color: #fff; }
.articles-section { padding: 60px 0; }
.articles-grid { display: grid; grid-template-columns: repeat(auto-fill, minmax(340px, 1fr)); gap: 28px; }
.article-card {
  background: var(--bg-card); border-radius: var(--radius);
  overflow: hidden; box-shadow: var(--shadow); border: 1px solid var(--border);
  transition: transform 0.25s, box-shadow 0.25s;
}
.article-card:hover { transform: translateY(-6px); box-shadow: var(--shadow-lg); }
.article-card-link { display: block; text-decoration: none; color: inherit; height: 100%; }
.article-card-link:focus-visible { outline: 2px solid var(--accent); outline-offset: -2px; border-radius: var(--radius); }
.article-img-wrap { position: relative; height: 220px; overflow: hidden; }
.article-img { width: 100%; height: 100%; object-fit: cover; transition: transform 0.4s; }
.article-card:hover .article-img { transform: scale(1.06); }
.article-category-badge {
  position: absolute; top: 14px; inset-inline-start: 14px;
  background: var(--accent); color: #fff;
  font-size: 12px; font-weight: 600;
  padding: 4px 12px; border-radius: 50px;
}
.article-content { padding: 24px; }
.article-meta { display: flex; gap: 16px; margin-bottom: 12px; flex-wrap: wrap; }
.article-meta-item { display: flex; align-items: center; gap: 5px; color: var(--text-muted); font-size: 13px; }
.article-title { font-size: 18px; font-weight: 700; color: var(--text); margin-bottom: 10px; line-height: 1.4; display: -webkit-box; -webkit-line-clamp: 2; -webkit-box-orient: vertical; overflow: hidden; }
.article-excerpt { color: var(--text-muted); font-size: 14px; line-height: 1.7; display: -webkit-box; -webkit-line-clamp: 3; -webkit-box-orient: vertical; overflow: hidden; margin-bottom: 16px; }
.article-footer { display: flex; align-items: center; justify-content: space-between; flex-wrap: wrap; gap: 8px; }
.article-date { color: var(--text-light); font-size: 13px; }
.article-read-more {
  display: flex; align-items: center; gap: 5px;
  color: var(--accent); font-size: 14px; font-weight: 600;
  background: transparent; border: none; cursor: pointer; padding: 0;
  transition: gap 0.2s;
}
.article-read-more:hover { gap: 10px; }

/* ===== ARTICLE DETAIL ===== */
.article-detail-hero { position: relative; height: 460px; display: flex; align-items: flex-end; overflow: hidden; }
.article-detail-hero-img { position: absolute; inset: 0; width: 100%; height: 100%; object-fit: cover; }
.article-detail-overlay { position: absolute; inset: 0; background: linear-gradient(to top, rgba(5,15,30,0.92) 0%, rgba(5,15,30,0.5) 50%, transparent 100%); }
.article-detail-hero-content { position: relative; z-index: 2; max-width: 860px; margin: 0 auto; padding: 0 24px 48px; width: 100%; }
.back-btn {
  display: inline-flex; align-items: center; gap: 6px;
  color: rgba(255,255,255,0.8); background: rgba(255,255,255,0.1);
  border: none; border-radius: 8px; padding: 8px 16px;
  font-size: 14px; cursor: pointer; margin-bottom: 24px; transition: background 0.2s;
}
.back-btn:hover { background: rgba(255,255,255,0.2); color: #fff; }
.article-detail-title { color: #fff; font-size: clamp(22px, 3.5vw, 38px); font-weight: 900; line-height: 1.3; margin-bottom: 16px; }
.article-detail-meta { display: flex; gap: 20px; flex-wrap: wrap; color: rgba(255,255,255,0.75); font-size: 14px; }
.article-detail-meta span { display: flex; align-items: center; gap: 6px; }
.article-detail-body { max-width: 860px; margin: 0 auto; padding: 56px 24px; }
.article-detail-content { margin-bottom: 40px; }
.article-body-h3 { font-size: 20px; font-weight: 700; color: var(--primary); margin: 28px 0 12px; }
.dark .article-body-h3 { color: var(--accent-light); }
.article-body-p { color: var(--text); font-size: 16px; line-height: 1.9; margin-bottom: 14px; }
.article-body-li {
  color: var(--text); font-size: 16px; line-height: 1.9;
  padding: 4px 0; padding-inline-start: 20px;
  list-style-type: none; position: relative;
}
.article-body-li::before { content: '•'; color: var(--accent); position: absolute; inset-inline-start: 0; font-weight: 900; }
.article-body-li.check::before { display: none; }
.article-share {
  display: flex; align-items: center; gap: 10px;
  color: var(--text-muted); font-size: 14px;
  padding: 20px 0;
  border-top: 1px solid var(--border); border-bottom: 1px solid var(--border);
  margin-bottom: 40px;
}
.related-articles { margin-top: 40px; }
.related-articles h2 { font-size: 22px; font-weight: 800; color: var(--text); margin-bottom: 20px; }
.related-grid { display: grid; grid-template-columns: repeat(auto-fill, minmax(220px, 1fr)); gap: 20px; }
.related-card { border-radius: var(--radius-sm); overflow: hidden; cursor: pointer; border: 1px solid var(--border); transition: transform 0.2s; background: var(--bg-card); }
.related-card:hover { transform: translateY(-4px); }
.related-img { width: 100%; height: 130px; object-fit: cover; }
.related-info { padding: 14px; }
.related-info h4 { font-size: 14px; font-weight: 600; color: var(--text); margin-bottom: 6px; line-height: 1.4; }
.related-info span { font-size: 12px; color: var(--text-muted); }

/* ===== NOT FOUND ===== */
.not-found { display: flex; flex-direction: column; align-items: center; justify-content: center; min-height: 60vh; gap: 20px; padding: 24px; }
.not-found h2 { font-size: 24px; color: var(--text); }

/* ===== FOOTER ===== */
.footer { background: #06111e; color: rgba(255,255,255,0.82); padding: 60px 0 0; }
.footer-inner {
  max-width: 1200px; margin: 0 auto; padding: 0 24px 48px;
  display: grid; grid-template-columns: 2fr 1fr 1fr 1.5fr; gap: 40px;
}
@media (max-width: 900px) { .footer-inner { grid-template-columns: 1fr 1fr; } }
@media (max-width: 560px) { .footer-inner { grid-template-columns: 1fr; } }
.footer-logo { display: flex; align-items: center; gap: 12px; text-decoration: none; color: #fff; font-size: 17px; font-weight: 700; margin-bottom: 16px; }
.footer-logo-img { width: 44px; height: 44px; object-fit: contain; border-radius: 8px; }
.footer-tagline { color: rgba(255,255,255,0.55); font-size: 14px; line-height: 1.7; margin-bottom: 20px; }

.social-icon {
  width: 38px; height: 38px; border-radius: 10px;
  background: rgba(255,255,255,0.08);
  display: flex; align-items: center; justify-content: center;
  color: rgba(255,255,255,0.7);
  transition: background 0.2s, color 0.2s; text-decoration: none;
}
.social-icon:hover { background: var(--accent); color: #fff; }

.footer-links h4, .footer-services h4, .footer-contact h4 {
  color: #fff; font-size: 15px; font-weight: 700;
  margin-bottom: 18px; padding-bottom: 10px;
  border-bottom: 2px solid var(--accent); display: inline-block;
}
.footer-links ul, .footer-services ul, .footer-contact ul { list-style: none; display: flex; flex-direction: column; gap: 10px; }
.footer-links a { color: rgba(255,255,255,0.65); text-decoration: none; font-size: 14px; transition: color 0.2s; }
.footer-links a:hover { color: var(--accent-light); }
.footer-services li { color: rgba(255,255,255,0.65); font-size: 14px; }
.footer-services a { color: rgba(255,255,255,0.65); text-decoration: none; font-size: 14px; transition: color 0.2s; }
.footer-services a:hover { color: var(--accent-light); }
.footer-contact-item { display: flex; align-items: center; gap: 10px; color: rgba(255,255,255,0.65); text-decoration: none; font-size: 14px; transition: color 0.2s; }
.footer-contact-item:hover { color: var(--accent-light); }
.footer-bottom {
  border-top: 1px solid rgba(255,255,255,0.08);
  padding: 20px 24px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  flex-wrap: wrap;
  max-width: 1200px;
  margin: 0 auto;
}
.footer-bottom p { margin: 0; font-size: 13px; color: rgba(255,255,255,0.4); }
.footer-bottom-map-link {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  color: var(--accent-light);
  text-decoration: none;
  font-size: 13px;
  font-weight: 600;
  padding: 8px 14px;
  border: 1px solid rgba(255,255,255,0.14);
  border-radius: 50px;
  transition: color 0.2s, border-color 0.2s, background 0.2s;
}
.footer-bottom-map-link:hover {
  color: #fff;
  border-color: var(--accent);
  background: rgba(232,119,34,0.12);
}

/* ===== CHATBOT ===== */
.chatbot-overlay {
  position: fixed; inset: 0; z-index: 1100;
  display: flex; align-items: flex-end; justify-content: flex-end;
  padding: 24px; pointer-events: none;
}
.chatbot-window {
  width: 380px; max-height: 560px;
  background: var(--bg-card); border-radius: 20px;
  box-shadow: var(--shadow-lg); border: 1px solid var(--border);
  display: flex; flex-direction: column; overflow: hidden;
  pointer-events: all;
  animation: slideUp 0.3s ease;
}
@keyframes slideUp {
  from { transform: translateY(30px); opacity: 0; }
  to { transform: translateY(0); opacity: 1; }
}
@media (max-width: 450px) {
  .chatbot-overlay { padding: 0; align-items: flex-end; }
  .chatbot-window { width: 100%; border-radius: 20px 20px 0 0; max-height: 70vh; }
}
.chatbot-header {
  background: linear-gradient(135deg, var(--primary), var(--primary-light));
  padding: 18px 20px;
  display: flex; align-items: center; justify-content: space-between;
  flex-shrink: 0;
}
.chatbot-title { display: flex; align-items: center; gap: 10px; color: #fff; font-size: 16px; font-weight: 700; }
.chatbot-close {
  background: rgba(255,255,255,0.15); border: none; border-radius: 8px;
  color: #fff; cursor: pointer; padding: 6px;
  display: flex; align-items: center; justify-content: center;
  transition: background 0.2s;
}
.chatbot-close:hover { background: rgba(255,255,255,0.25); }
.chatbot-body { flex: 1; overflow-y: auto; padding: 16px; display: flex; flex-direction: column; gap: 12px; }
.chat-msg { display: flex; gap: 8px; align-items: flex-start; }
.chat-msg.bot { flex-direction: row; }
.chat-msg.user { flex-direction: row-reverse; }
.chat-avatar { width: 32px; height: 32px; border-radius: 50%; display: flex; align-items: center; justify-content: center; flex-shrink: 0; }
.chat-msg.bot .chat-avatar { background: var(--primary); color: #fff; }
.chat-msg.user .chat-avatar { background: var(--accent); color: #fff; }
.chat-bubble { max-width: 78%; padding: 10px 14px; border-radius: 14px; font-size: 14px; line-height: 1.65; }
.chat-msg.bot .chat-bubble { background: var(--bg-section); color: var(--text); border-bottom-start-radius: 4px; }
.chat-msg.user .chat-bubble { background: var(--accent); color: #fff; border-bottom-end-radius: 4px; }
.quick-replies { padding: 8px 0; display: flex; flex-direction: column; gap: 7px; }
.quick-label { font-size: 12px; color: var(--text-muted); margin-bottom: 4px; }
.quick-reply-btn {
  background: var(--bg); border: 1.5px solid var(--border);
  border-radius: 20px; padding: 8px 14px;
  font-size: 13px; text-align: start; cursor: pointer;
  color: var(--text); transition: all 0.2s; font-family: inherit;
}
.quick-reply-btn:hover { border-color: var(--accent); color: var(--accent); background: rgba(232,119,34,0.05); }
.chatbot-footer { padding: 14px 16px; border-top: 1px solid var(--border); display: flex; gap: 10px; flex-shrink: 0; }
.chat-input {
  flex: 1; padding: 10px 14px; border-radius: 20px;
  border: 1.5px solid var(--border); background: var(--bg);
  color: var(--text); font-size: 14px; outline: none; font-family: inherit;
  transition: border-color 0.2s;
}
.chat-input:focus { border-color: var(--accent); }
.chat-send-btn {
  width: 40px; height: 40px; border-radius: 50%;
  background: var(--accent); border: none; color: #fff;
  display: flex; align-items: center; justify-content: center;
  cursor: pointer; flex-shrink: 0; transition: background 0.2s;
}
.chat-send-btn:hover { background: var(--accent-dark); }

/* ===== (scroll-reveal handled by .page-rise) ===== */

/* ===== HOME HERO ANIMATION ===== */
/* (hero entrance handled by .page-rise) */

/* Hero dots */
.hero-dots {
  display: flex;
  gap: 10px;
  margin-top: 28px;
  justify-content: flex-start;
}
[dir="rtl"] .hero-dots { justify-content: flex-start; }
.hero-dot {
  width: 10px;
  height: 10px;
  border-radius: 50%;
  border: 2px solid rgba(255,255,255,0.6);
  background: transparent;
  cursor: pointer;
  padding: 0;
  transition: background 0.3s, border-color 0.3s, transform 0.3s;
}
.hero-dot:hover { border-color: #fff; transform: scale(1.2); }
.hero-dot-active { background: var(--accent); border-color: var(--accent); transform: scale(1.15); }

/* ===== WHATSAPP SUPPORT BUTTON (inside chat) ===== */
.chat-whatsapp-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  margin: 0 16px 12px;
  padding: 10px 16px;
  background: #25D366;
  color: #fff;
  border-radius: 50px;
  font-size: 13px;
  font-weight: 700;
  text-decoration: none;
  transition: background 0.2s, transform 0.2s;
  flex-shrink: 0;
}
.chat-whatsapp-btn:hover { background: #1da851; transform: translateY(-1px); }

/* ===== FLOATING BUTTONS GROUP ===== */
.float-btns {
  position: fixed;
  bottom: 28px;
  inset-inline-end: 28px;
  z-index: 1050;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
}

/* Chat / Assistant float button */
.float-chat-btn {
  width: 54px;
  height: 54px;
  border-radius: 50%;
  border: none;
  cursor: pointer;
  background: var(--primary);
  color: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 4px 18px rgba(26,74,122,0.45);
  transition: transform 0.22s, box-shadow 0.22s, background 0.2s;
}
.float-chat-btn:hover {
  transform: scale(1.12);
  background: var(--primary-light);
  box-shadow: 0 8px 28px rgba(26,74,122,0.55);
}

/* WhatsApp float button */
.whatsapp-float {
  width: 58px;
  height: 58px;
  border-radius: 50%;
  background: #25D366;
  color: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 4px 20px rgba(37,211,102,0.45);
  text-decoration: none;
  transition: transform 0.22s, box-shadow 0.22s;
  animation: waPulse 2.5s infinite;
}
.whatsapp-float:hover {
  transform: scale(1.12);
  box-shadow: 0 8px 32px rgba(37,211,102,0.6);
  animation: none;
}
@keyframes waPulse {
  0%, 100% { box-shadow: 0 4px 20px rgba(37,211,102,0.45); }
  50% { box-shadow: 0 4px 32px rgba(37,211,102,0.7), 0 0 0 10px rgba(37,211,102,0.12); }
}

/* ===== HOME SEARCH SECTION ===== */
.home-search-section {
  background: var(--bg-card);
  border-bottom: 1px solid var(--border);
  padding: 28px 24px;
}
.home-search-inner {
  max-width: 680px;
  margin: 0 auto;
  display: flex;
  flex-direction: column;
  gap: 14px;
}
.home-search-label {
  font-size: 0.85rem;
  color: var(--text-muted);
  font-weight: 600;
  text-align: center;
  letter-spacing: 0.05em;
  text-transform: uppercase;
  margin: 0;
}
.home-search-wrap {
  position: relative;
}
.home-search-box {
  display: flex;
  align-items: center;
  gap: 10px;
  background: var(--bg);
  border: 1.5px solid var(--border);
  border-radius: 12px;
  padding: 0 14px;
  transition: border-color 0.2s, box-shadow 0.2s;
}
.home-search-box:focus-within {
  border-color: var(--primary);
  box-shadow: 0 0 0 3px rgba(26,74,122,0.12);
}
.home-search-icon { color: var(--text-muted); flex-shrink: 0; }
.home-search-input {
  flex: 1;
  border: none;
  outline: none;
  background: transparent;
  font-size: 1rem;
  color: var(--text);
  padding: 14px 0;

}
.home-search-clear {
  background: none;
  border: none;
  cursor: pointer;
  color: var(--text-muted);
  display: flex;
  align-items: center;
  padding: 4px;
  border-radius: 50%;
  transition: color 0.2s;
}
.home-search-clear:hover { color: var(--text); }

/* Dropdown results */
.home-search-results {
  position: absolute;
  top: calc(100% + 6px);
  left: 0; right: 0;
  background: var(--bg-card);
  border: 1px solid var(--border);
  border-radius: 12px;
  list-style: none;
  margin: 0; padding: 6px;
  z-index: 200;
  box-shadow: 0 12px 40px rgba(0,0,0,0.15);
  max-height: 320px;
  overflow-y: auto;
}
.home-search-result-item {
  display: flex;
  align-items: center;
  gap: 12px;
  width: 100%;
  background: none;
  border: none;
  cursor: pointer;
  padding: 10px 10px;
  border-radius: 8px;
  text-align: start;
  transition: background 0.15s;
}
.home-search-result-item:hover { background: var(--bg); }
.search-result-img {
  width: 44px;
  height: 44px;
  border-radius: 8px;
  object-fit: cover;
  flex-shrink: 0;
}
.search-result-title {
  display: block;
  font-size: 0.9rem;
  font-weight: 600;
  color: var(--text);
  line-height: 1.3;
}
.search-result-cat {
  display: block;
  font-size: 0.75rem;
  color: var(--accent);
  margin-top: 2px;
}
.search-result-arrow {
  margin-inline-start: auto;
  color: var(--text-muted);
  flex-shrink: 0;
}
.home-search-empty {
  position: absolute;
  top: calc(100% + 6px);
  left: 0; right: 0;
  background: var(--bg-card);
  border: 1px solid var(--border);
  border-radius: 12px;
  padding: 16px;
  text-align: center;
  color: var(--text-muted);
  font-size: 0.9rem;
  box-shadow: 0 8px 24px rgba(0,0,0,0.1);
}

/* Quick links row */
.home-search-quick {
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
  justify-content: center;
}
.home-search-quick-label {
  font-size: 0.82rem;
  color: var(--text-muted);
}
.home-search-quick-btn {
  padding: 7px 18px;
  border-radius: 20px;
  border: 1.5px solid var(--primary);
  background: var(--primary);
  color: #fff;

  font-size: 0.88rem;
  font-weight: 600;
  cursor: pointer;
  transition: background 0.2s, transform 0.15s;
}
.home-search-quick-btn:hover { background: var(--primary-light); transform: translateY(-1px); }
.home-search-quick-btn--outline {
  background: transparent;
  color: var(--primary);
}
.home-search-quick-btn--outline:hover {
  background: var(--primary);
  color: #fff;
}

/* ===== SCROLL TO TOP BUTTON ===== */
.scroll-top-btn {
  position: fixed;
  inset-inline-start: 24px;
  bottom: 110px;
  z-index: 999;
  width: 46px;
  height: 46px;
  border-radius: 50%;
  border: none;
  background: var(--accent);
  color: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  box-shadow: 0 4px 18px rgba(249,115,22,0.45);
  opacity: 0;
  transform: translateY(20px) scale(0.85);
  pointer-events: none;
  transition: opacity 0.3s, transform 0.3s, box-shadow 0.2s;
}
.scroll-top-btn--visible {
  opacity: 1;
  transform: translateY(0) scale(1);
  pointer-events: auto;
}
.scroll-top-btn:hover {
  box-shadow: 0 8px 28px rgba(249,115,22,0.6);
  transform: translateY(-3px) scale(1.08);
}

/* ===== CLIENTS PAGE ===== */
.clients-page {}

.clients-section-header {
  text-align: center;
  margin-bottom: 48px;
}
.clients-section-badge {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 6px 18px;
  border-radius: 50px;
  font-size: 0.85rem;
  font-weight: 700;
  margin-bottom: 14px;
  letter-spacing: 0.02em;
}
.clients-badge-outside {
  background: rgba(37,99,168,0.12);
  color: var(--primary-light);
  border: 1px solid rgba(37,99,168,0.25);
}
.clients-badge-inside {
  background: rgba(232,119,34,0.12);
  color: var(--accent);
  border: 1px solid rgba(232,119,34,0.25);
}
.clients-section-title {
  font-size: clamp(1.6rem, 3vw, 2.2rem);
  font-weight: 800;
  color: var(--text);
  margin-bottom: 10px;
}
.clients-section-sub {
  color: var(--text-muted);
  font-size: 1.05rem;
  max-width: 520px;
  margin: 0 auto;
}

/* Outside Egypt */
.clients-outside-section { background: var(--bg-section); }
.clients-outside-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  gap: 24px;
  max-width: 700px;
  margin: 0 auto;
}
.clients-country-card {
  background: var(--bg-card);
  border-radius: var(--radius);
  padding: 36px 28px;
  text-align: center;
  box-shadow: var(--shadow);
  border: 1px solid var(--border);
  transition: transform 0.22s, box-shadow 0.22s;
}
.clients-country-card:hover {
  transform: translateY(-5px);
  box-shadow: var(--shadow-lg);
}
.clients-country-flag {
  font-size: 3rem;
  margin-bottom: 14px;
  line-height: 1;
}
.clients-country-card h3 {
  font-size: 1.2rem;
  font-weight: 700;
  color: var(--text);
  margin-bottom: 8px;
}
.clients-country-card p {
  color: var(--text-muted);
  font-size: 0.92rem;
  line-height: 1.6;
}

/* Inside Egypt, Sectors */
.clients-inside-section { background: var(--bg); }
.clients-sectors {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 28px;
}
.clients-sector-card {
  background: var(--bg-card);
  border-radius: var(--radius);
  box-shadow: var(--shadow);
  border: 1px solid var(--border);
  overflow: hidden;
  transition: transform 0.22s, box-shadow 0.22s;
}
.clients-sector-card:hover {
  transform: translateY(-4px);
  box-shadow: var(--shadow-lg);
}
.clients-sector-header {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 20px 24px;
  border-bottom: 3px solid;
  background: var(--bg-section);
}
.clients-sector-dot {
  width: 12px;
  height: 12px;
  border-radius: 50%;
  flex-shrink: 0;
}
.clients-sector-header h3 {
  font-size: 1rem;
  font-weight: 700;
  line-height: 1.4;
}
.clients-sector-body {
  padding: 20px 24px;
}
.clients-subsector {
  margin-bottom: 18px;
}
.clients-subsector:last-child { margin-bottom: 0; }
.clients-subsector-name {
  font-size: 0.82rem;
  font-weight: 700;
  color: var(--text-muted);
  text-transform: uppercase;
  letter-spacing: 0.06em;
  margin-bottom: 10px;
  padding-bottom: 6px;
  border-bottom: 1px dashed var(--border);
}
.clients-company-list {
  list-style: none;
  display: flex;
  flex-direction: column;
  gap: 7px;
}
.clients-company-item {
  display: flex;
  align-items: center;
  gap: 10px;
  font-size: 0.93rem;
  color: var(--text);
  line-height: 1.4;
}
.clients-company-dot {
  width: 7px;
  height: 7px;
  border-radius: 50%;
  flex-shrink: 0;
  opacity: 0.75;
}
.clients-company-with-logo {
  display: flex;
  align-items: center;
  gap: 10px;
}
.clients-company-logo {
  width: 24px;
  height: 24px;
  object-fit: contain;
  flex-shrink: 0;
  border-radius: 2px;
}
.clients-company-dot-hidden {
  display: none;
}
/* Sector card image */
.clients-sector-img-wrap {
  position: relative;
  height: 160px;
  overflow: hidden;
}
.clients-sector-img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
  transition: transform 0.5s ease;
}
.clients-sector-card:hover .clients-sector-img {
  transform: scale(1.06);
}
.clients-sector-img-overlay {
  position: absolute;
  inset: 0;
  opacity: 0.55;
}
.clients-sector-img-title {
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  padding: 12px 18px;
  color: #fff;
  font-size: 0.95rem;
  font-weight: 700;
  line-height: 1.35;
  text-shadow: 0 1px 6px rgba(0,0,0,0.5);
  background: linear-gradient(to top, rgba(0,0,0,0.55), transparent);
}

/* Gulf card image */
.clients-gulf-card {
  background: var(--bg-card);
  border-radius: var(--radius);
  box-shadow: var(--shadow);
  border: 1px solid var(--border);
  overflow: hidden;
  max-width: 900px;
  margin: 0 auto;
}
.clients-gulf-img-wrap {
  position: relative;
  height: 200px;
  overflow: hidden;
}
.clients-gulf-img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
  transition: transform 0.5s ease;
}
.clients-gulf-card:hover .clients-gulf-img {
  transform: scale(1.04);
}
.clients-gulf-img-overlay {
  position: absolute;
  inset: 0;
  background: linear-gradient(135deg, rgba(245,158,11,0.6), rgba(15,45,79,0.7));
}
.clients-gulf-img-label {
  position: absolute;
  bottom: 18px;
  left: 24px;
  right: 24px;
  display: flex;
  align-items: center;
  gap: 10px;
  color: #fff;
  font-size: 1.15rem;
  font-weight: 700;
  text-shadow: 0 2px 8px rgba(0,0,0,0.4);
}
.clients-gulf-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(220px, 1fr));
  gap: 8px 20px;
}
@media (max-width: 600px) {
  .clients-sectors { grid-template-columns: 1fr; }
  .clients-gulf-grid { grid-template-columns: 1fr; }
  .clients-sector-img-wrap { height: 120px; }
  .clients-gulf-img-wrap { height: 150px; }
}

/* ══════════════════════════════════════════
   Company Card, logo + name + specialty
   ══════════════════════════════════════════ */
.co-card {
  display: flex;
  align-items: center;
  gap: 12px;
  background: var(--bg-card);
  border: 1px solid var(--border);
  border-radius: 10px;
  padding: 11px 14px;
  transition: transform 0.18s, box-shadow 0.18s;
}
.co-card:hover {
  transform: translateY(-2px);
  box-shadow: var(--shadow);
}
.co-card-logo-wrap {
  position: relative;
  width: 44px;
  height: 44px;
  flex-shrink: 0;
}
.co-card-logo {
  width: 44px;
  height: 44px;
  object-fit: contain;
  border-radius: 8px;
  background: #fff;
  padding: 3px;
  border: 1px solid var(--border);
}
.co-card-initials {
  width: 44px;
  height: 44px;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.82rem;
  font-weight: 800;
  color: #fff;
  letter-spacing: 0.02em;
  text-transform: uppercase;
}
.co-card-info {
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
}
.co-card-name {
  font-size: 0.88rem;
  font-weight: 700;
  color: var(--text);
  line-height: 1.35;
  white-space: normal;
}
.co-card-specialty {
  font-size: 0.75rem;
  color: var(--text-muted);
  line-height: 1.3;
}
.co-card-location {
  display: inline-flex;
  align-items: center;
  gap: 3px;
  font-size: 0.72rem;
  color: var(--primary);
  font-weight: 600;
  margin-top: 2px;
}

/* ══════════════════════════════════════════
   Sector Block, header image + card grid
   ══════════════════════════════════════════ */
.sectors-list {
  display: flex;
  flex-direction: column;
  gap: 36px;
}
.sector-block {
  background: var(--bg-card);
  border: 1px solid var(--border);
  border-radius: var(--radius);
  box-shadow: var(--shadow);
  overflow: hidden;
}
.sector-block-header {
  display: flex;
  align-items: center;
  gap: 0;
  border-left: 4px solid;
}
[dir="rtl"] .sector-block-header { border-left: none; border-right: 4px solid; }
.sector-block-img-wrap {
  position: relative;
  width: 120px;
  min-height: 72px;
  flex-shrink: 0;
  overflow: hidden;
}
.sector-block-img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
}
.sector-block-img-overlay {
  position: absolute;
  inset: 0;
  opacity: 0.5;
}
.sector-block-title-wrap {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 16px 20px;
  flex: 1;
}
.sector-block-dot {
  width: 10px;
  height: 10px;
  border-radius: 50%;
  flex-shrink: 0;
}
.sector-block-title {
  font-size: 1.05rem;
  font-weight: 800;
  flex: 1;
  line-height: 1.3;
}
.sector-block-count {
  font-size: 0.72rem;
  font-weight: 700;
  color: #fff;
  padding: 2px 9px;
  border-radius: 20px;
  white-space: nowrap;
}
.sector-block-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(230px, 1fr));
  gap: 10px;
  padding: 16px;
  border-top: 1px solid var(--border);
  background: var(--bg-section);
}

/* ══════════════════════════════════════════
   Gulf Block
   ══════════════════════════════════════════ */
.gulf-block {
  background: var(--bg-card);
  border: 1px solid var(--border);
  border-radius: var(--radius);
  box-shadow: var(--shadow);
  overflow: hidden;
}
.gulf-block-hero-wrap {
  position: relative;
  height: 200px;
  overflow: hidden;
}
.gulf-block-hero-img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
}
.gulf-block-hero-overlay {
  position: absolute;
  inset: 0;
  background: linear-gradient(135deg, rgba(8,145,178,0.65), rgba(15,45,79,0.75));
}
.gulf-block-hero-label {
  position: absolute;
  bottom: 20px;
  left: 28px;
  right: 28px;
  display: flex;
  align-items: center;
  gap: 10px;
  color: #fff;
  font-size: 1.25rem;
  font-weight: 800;
  text-shadow: 0 2px 8px rgba(0,0,0,0.4);
}
.gulf-companies-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(260px, 1fr));
  gap: 10px;
  padding: 18px;
  background: var(--bg-section);
}

@media (max-width: 700px) {
  .sector-block-grid { grid-template-columns: 1fr; }
  .gulf-companies-grid { grid-template-columns: 1fr; }
  .sector-block-img-wrap { width: 80px; min-height: 60px; }
  .gulf-block-hero-wrap { height: 140px; }
  .co-card-logo-wrap, .co-card-logo, .co-card-initials { width: 36px; height: 36px; }
}
/* ===== NEW ABOUT DESIGN ===== */
.about-hero { position: relative; background: linear-gradient(135deg, var(--primary) 0%, #0e2540 100%); color: #fff; padding: 120px 24px 100px; text-align: center; overflow: hidden; }
.about-hero-inner { max-width: 900px; margin: 0 auto; z-index: 2; position: relative; }
.about-hero-logo-wrap { display: inline-flex; align-items: center; gap: 16px; margin-bottom: 32px; }
.about-hero-logo { width: 64px; height: 64px; object-fit: contain; filter: drop-shadow(0 8px 24px rgba(0,0,0,0.35)); }
.about-hero-word { font-family: Georgia, serif; font-weight: 700; font-size: 2rem; letter-spacing: 0.02em; line-height: 1; }
.about-hero-main { color: #eeb603; font-size: 2.8rem; display: block; }
.about-hero-sub { font-size: 1.1rem; opacity: 0.85; font-weight: 400; }
.about-hero-title { font-family: Georgia, serif; font-size: clamp(2rem, 5vw, 3.6rem); line-height: 1.15; margin: 12px 0 18px; letter-spacing: -0.03em; }
.about-hero-desc { font-size: 1.15rem; opacity: 0.85; max-width: 640px; margin: 0 auto; line-height: 1.65; }

.about-block { background: var(--bg); padding: 80px 24px; }
.about-block-inner { max-width: 1000px; margin: 0 auto; }
.about-block-label { font-family: Georgia, serif; font-size: 0.85rem; letter-spacing: 0.15em; text-transform: uppercase; color: var(--accent); margin-bottom: 6px; }
.about-block-title { font-family: Georgia, serif; font-size: clamp(1.8rem, 4vw, 2.8rem); letter-spacing: -0.03em; margin-bottom: 40px; line-height: 1.2; }

.about-story-text { font-size: 1.15rem; line-height: 1.75; color: var(--text); max-width: 780px; }

.about-step-grid { display: grid; gap: 32px; }
.about-step { display: flex; gap: 24px; align-items: flex-start; padding: 24px 0; border-bottom: 1px solid var(--border); }
.about-step--right { flex-direction: row-reverse; text-align: right; }
.about-step-icon-wrap { flex-shrink: 0; width: 56px; height: 56px; border-radius: 16px; background: linear-gradient(135deg, var(--primary), #0e2540); color: #fff; display: grid; place-items: center; box-shadow: 0 8px 24px rgba(26,74,122,0.2); }
.about-step-content strong { font-family: Georgia, serif; font-size: 1.15rem; display: block; margin-bottom: 6px; }
.about-step-content p { color: var(--text-soft); font-size: 0.95rem; line-height: 1.65; }

.about-stats { background: linear-gradient(135deg, var(--bg-card), var(--bg-section)); padding: 60px 24px; }
.about-stat-row { max-width: 1000px; margin: 0 auto; display: grid; grid-template-columns: repeat(auto-fit,minmax(140px,1fr)); gap: 20px; }
.about-stat { background: var(--bg); border: 1px solid var(--border); border-radius: 16px; padding: 28px 18px; text-align: center; transition: transform .2s; }
.about-stat:hover { transform: translateY(-4px); }
.about-stat--alt { background: var(--primary); color: #fff; border-color: var(--primary); }
.about-stat-num { font-family: Georgia, serif; font-size: 2rem; font-weight: 700; color: var(--primary); font-variant-numeric: tabular-nums; }
.about-stat--alt .about-stat-num { color: #eeb603; }
.about-stat-desc { font-size: 0.85rem; opacity: 0.8; margin-top: 4px; }

.about-values-grid { display: grid; gap: 24px; grid-template-columns: repeat(auto-fit, minmax(220px, 1fr)); }
.about-value { background: var(--bg-card); border: 1px solid var(--border); border-radius: 16px; padding: 28px 24px; transition: box-shadow .2s; }
.about-value:hover { box-shadow: 0 12px 40px rgba(26,74,122,0.08); }
.about-value-icon { width: 48px; height: 48px; border-radius: 12px; background: linear-gradient(135deg, var(--primary), #0e2540); color: #fff; display: grid; place-items: center; margin-bottom: 14px; }
.about-value strong { display: block; font-family: Georgia, serif; font-size: 1.05rem; margin-bottom: 6px; }
.about-value p { font-size: 0.9rem; color: var(--text-soft); line-height: 1.5; }

.about-cta { padding: 60px 24px; text-align: center; background: linear-gradient(135deg, var(--primary) 0%, #0e2540 100%); }
.about-cta-btn { display: inline-block; background: #eeb603; color: #0e2540; padding: 14px 36px; border-radius: 50px; font-weight: 700; text-decoration: none; transition: transform .15s; }
.about-cta-btn:hover { transform: translateY(-3px); }
/* ===== CONTACT PAGE UI ===== */
.contact-hero {
  background: linear-gradient(135deg, var(--primary) 0%, var(--primary-dark) 100%);
  color: #fff;
  padding: 88px 24px 64px;
  text-align: center;
  position: relative;
  overflow: hidden;
}
.contact-hero::after {
  content: '';
  position: absolute;
  inset: 0;
  background: radial-gradient(900px 400px at 50% -10%, rgba(232,119,34,0.22), transparent 60%);
  pointer-events: none;
}
.contact-hero-inner { max-width: 720px; margin: 0 auto; position: relative; z-index: 1; }
.contact-hero-title { font-size: clamp(2rem, 5vw, 3.2rem); line-height: 1.15; font-weight: 800; margin: 0 0 14px; letter-spacing: -0.02em; }
.contact-hero-desc { font-size: 1.1rem; opacity: 0.92; line-height: 1.7; margin: 0 0 28px; max-width: 52ch; margin-inline: auto; }

/* Channels grid (aligned, consistent cells) */
.contact-channels { padding-bottom: 48px; }
.channel-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 16px;
  max-width: 920px;
  margin: 0 auto;
}
@media (max-width: 720px) { .channel-grid { grid-template-columns: 1fr; } }
.channel-card {
  display: flex;
  align-items: flex-start;
  gap: 16px;
  background: var(--bg-card);
  border: 1px solid var(--border);
  border-radius: var(--radius);
  padding: 22px 22px 20px;
  min-height: 150px;
  box-shadow: var(--shadow-sm);
  transition: transform 0.2s ease, box-shadow 0.2s ease, border-color 0.2s ease;
}
.channel-card:hover {
  transform: translateY(-3px);
  box-shadow: var(--shadow-md);
  border-color: var(--navy-300);
}
.channel-icon {
  flex-shrink: 0;
  width: 48px;
  height: 48px;
  border-radius: 12px;
  background: linear-gradient(135deg, var(--primary), var(--primary-dark));
  color: #fff;
  display: grid;
  place-items: center;
  box-shadow: var(--shadow-sm);
}
.channel-body { min-width: 0; }
.channel-label {
  display: block;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  color: var(--text-muted);
  margin-bottom: 10px;
}
.channel-lines { display: flex; flex-direction: column; gap: 6px; }
.channel-link {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  color: var(--text);
  text-decoration: none;
  font-size: 0.95rem;
  font-weight: 600;
  line-height: 1.5;
  padding: 2px 0;
  transition: color 0.2s ease;
  word-break: break-word;
}
.channel-link:hover { color: var(--accent-dark); text-decoration: underline; text-underline-offset: 3px; }
.channel-tag {
  display: inline-flex;
  align-items: center;
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: var(--navy-500);
  background: var(--navy-100);
  border: 1px solid var(--navy-200);
  border-radius: 50px;
  padding: 2px 8px;
  white-space: nowrap;
}
.dark .channel-tag { background: var(--navy-700); color: var(--accent-light); border-color: var(--navy-600); }

.contact-wa-strip { display: flex; justify-content: center; margin-top: 28px; }

/* Branches + map */
.contact-branches { padding-top: 64px; }
.branch-layout {
  display: grid;
  grid-template-columns: 1fr;
  gap: 24px;
  align-items: stretch;
}
@media (min-width: 900px) {
  .branch-layout { grid-template-columns: 1.15fr 1fr; gap: 28px; }
}
.branch-map {
  position: relative;
  min-height: 380px;
  border-radius: var(--radius-lg);
  overflow: hidden;
  border: 1px solid var(--border);
  background: var(--bg-section);
  box-shadow: var(--shadow-md);
}
.branch-map iframe { display: block; border-radius: 0; }
.branch-list {
  list-style: none;
  display: flex;
  flex-direction: column;
  gap: 12px;
  margin: 0;
  padding: 0;
}
.branch-row {
  display: flex;
  align-items: center;
  gap: 14px;
  background: var(--bg-card);
  border: 1px solid var(--border);
  border-radius: var(--radius);
  padding: 14px 16px;
  box-shadow: var(--shadow-sm);
  transition: transform 0.2s ease, box-shadow 0.2s ease, border-color 0.2s ease;
}
.branch-row:hover { transform: translateY(-2px); box-shadow: var(--shadow-md); border-color: var(--navy-300); }
.branch-row-icon {
  flex-shrink: 0;
  width: 44px;
  height: 44px;
  border-radius: 12px;
  background: var(--bg-section);
  color: var(--navy-500);
  display: grid;
  place-items: center;
}
.dark .branch-row-icon { color: var(--accent-light); }
.branch-row-text { min-width: 0; flex: 1; }
.branch-row-text strong { display: block; font-size: 0.95rem; line-height: 1.45; color: var(--text); }
.branch-row-text span { display: block; font-size: 12px; color: var(--text-muted); margin-top: 3px; }
.branch-row-link {
  flex-shrink: 0;
  display: inline-flex;
  align-items: center;
  gap: 5px;
  font-size: 12px;
  font-weight: 700;
  color: var(--accent-dark);
  text-decoration: none;
  padding: 7px 13px;
  border: 1px solid var(--accent);
  border-radius: 50px;
  white-space: nowrap;
  transition: background 0.2s ease, color 0.2s ease;
}
.branch-row-link:hover { background: var(--accent); color: #fff; }

.contact-map-fallback {
  position: absolute;
  bottom: 12px;
  inset-inline-end: 12px;
  z-index: 3;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  background: rgba(2, 49, 81, 0.92);
  color: #fff;
  font-size: 12px;
  font-weight: 700;
  padding: 8px 14px;
  border-radius: 50px;
  text-decoration: none;
  box-shadow: var(--shadow-md);
  transition: transform 0.15s ease, filter 0.2s ease;
}
.contact-map-fallback:hover { transform: translateY(-2px); filter: brightness(1.15); }

/* CTA band */
.contact-cta {
  text-align: center;
  background: linear-gradient(135deg, var(--primary-dark) 0%, var(--primary) 100%);
  color: #fff;
  padding: 64px 24px;
}
.contact-cta-inner {
  max-width: 640px;
  margin: 0 auto;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 14px;
}
.contact-cta-icon {
  width: 60px;
  height: 60px;
  border-radius: 16px;
  background: rgba(255, 255, 255, 0.1);
  color: var(--accent-light);
  display: grid;
  place-items: center;
}
.contact-cta-text h2 { font-size: clamp(1.5rem, 4vw, 2.1rem); margin: 0 0 6px; letter-spacing: -0.02em; color: #fff; }
.contact-cta-text p { opacity: 0.9; margin: 0; font-size: 1rem; line-height: 1.7; }
.contact-cta-btns { display: flex; gap: 12px; flex-wrap: wrap; justify-content: center; margin-top: 6px; }

.btn-whatsapp-big {
  background: #25D366;
  color: #fff;
  padding: 13px 28px;
  border-radius: 50px;
  font-weight: 800;
  font-size: 1rem;
  text-decoration: none;
  display: inline-flex;
  align-items: center;
  gap: 10px;
  transition: transform 0.15s, filter 0.2s;
  box-shadow: var(--shadow-md);
}
.btn-whatsapp-big:hover { transform: translateY(-3px); filter: brightness(1.12); }

.btn-outline-contact {
  background: transparent;
  color: #fff;
  border: 2px solid rgba(255, 255, 255, 0.5);
  padding: 12px 28px;
  border-radius: 50px;
  font-weight: 700;
  font-size: 1rem;
  text-decoration: none;
  display: inline-flex;
  align-items: center;
  gap: 10px;
  transition: transform 0.15s, border-color 0.2s, background 0.2s;
}
.btn-outline-contact:hover { transform: translateY(-3px); border-color: #fff; background: rgba(255, 255, 255, 0.08); }

/* ===== ABOUT PAGE ENHANCEMENTS ===== */
.about-cta-btn {
  display: inline-flex;
  align-items: center;
  gap: 10px;
  background: #eeb603;
  color: var(--navy-700);
  padding: 14px 32px;
  border-radius: 50px;
  font-weight: 800;
  text-decoration: none;
  font-size: 16px;
  transition: transform .15s, box-shadow .2s, filter .2s;
  box-shadow: var(--shadow-md);
}
.about-cta-btn:hover {
  transform: translateY(-3px);
  box-shadow: var(--shadow-lg);
  filter: brightness(1.1);
}
.about-cta-btn:focus-visible {
  outline: 3px solid #eeb603;
  outline-offset: 3px;
}

.about-hero-bg {
  position: absolute;
  inset: 0;
  z-index: 1;
  opacity: 0.45;
  pointer-events: none;
}
.about-hero .about-hero-bg::after {
  content: '';
  position: absolute;
  inset: 0;
  background: linear-gradient(135deg, rgba(2,49,81,0.88) 0%, rgba(2,37,61,0.78) 60%, rgba(0,8,13,0.55) 100%);
}
.about-hero-inner {
  position: relative;
  z-index: 2;
}
.about-cta-inner {
  max-width: 720px;
  margin: 0 auto;
}

.about-section-divider {
  height: 1px;
  background: linear-gradient(90deg, transparent, var(--border), transparent);
  margin: var(--space-12) 0;
  border: none;
}

/* ===== GLOBAL FOCUS RING (keyboard a11y) ===== */
:focus-visible {
  outline: 2px solid var(--accent);
  outline-offset: 2px;
}
.float-chat-btn:focus-visible,
.whatsapp-float:focus-visible {
  outline-color: var(--accent-light);
}

/* Reduced motion */
@media (prefers-reduced-motion: reduce) {
  *, *::before, *::after {
    animation-duration: 0.01ms !important;
    animation-iteration-count: 1 !important;
    transition-duration: 0.01ms !important;
  }
  html { scroll-behavior: auto; }
}
